import * as d3 from "d3"
import { DateTime } from "luxon"
import { parseCoordinate } from "../../../helpers/chart_helpers"
import { capitalize } from "../../../helpers/string_helpers"

export default class AxisConfiguration {
    constructor(attributes) {
        this.dimension = attributes.dimension
        this.key = attributes.key
        this.label = attributes.label
        this.scaleType = attributes.scale_type
        this.tickCount = attributes.tick_count
        this.tickFormat = attributes.tick_format
        this.position = attributes.position
        this.grid = attributes.grid
        this.nice = attributes.nice
        this.options = attributes.options || {}
    }

    get scaleTypeName() {
        return capitalize(this.scaleType)
    }

    parsedValue(value) {
        if (this.scaleType === "band") return value

        return parseCoordinate(value, this.scaleTypeName)
    }

    domainFor(points, valueIndex) {
        if (this.scaleType === "band") return this.bandDomainFor(points, valueIndex)
        if (this.options.domain) return this.options.domain.map(value => this.parsedValue(value))
        if (points.length === 0) return this.emptyDomain()

        const domain = d3.extent(points, point => this.parsedValue(point[valueIndex]))
        if (this.options.min !== undefined) return [this.options.min, Math.max(domain[1], this.options.min + 1)]

        return domain
    }

    bandDomainFor(points, valueIndex) {
        return this.options.bands || Array.from(new Set(points.map(point => this.parsedValue(point[valueIndex]))))
    }

    emptyDomain() {
        if (this.options.empty_domain) return this.options.empty_domain.map(value => this.parsedValue(value))

        return this.scaleType === "time" ? [new Date(0), new Date(1000)] : [0, 1]
    }

    scaleFor(domain, range) {
        if (this.scaleType === "band") {
            return d3.scaleBand()
                .domain(domain)
                .range(range)
                .padding(this.options.padding ?? 0.2)
        }

        const scale = d3[`scale${this.scaleTypeName}`]()
            .domain(domain)
            .range(range)

        return this.nice ? scale.nice() : scale
    }

    ticksFor(scale) {
        if (this.scaleType === "band") return scale.domain()

        return scale.ticks(this.tickCount)
    }

    axisFormatter(scale) {
        if (this.scaleType === "band") return value => value
        if (this.tickFormat) return this.tickFormatter()

        return scale.tickFormat(this.tickCount)
    }

    valueFormatter(scale) {
        if (this.scaleType === "band") return value => value
        if (this.scaleType === "time") return this.timeFormatter()
        if (this.tickFormat === "m") return value => `${d3.format(".2f")(value / 60)}m`
        if (this.tickFormat) return d3.format(this.tickFormat)

        return scale.tickFormat(this.tickCount)
    }

    tickFormatter() {
        if (this.scaleType === "time") return this.timeFormatter()
        if (this.tickFormat === "m") return value => `${d3.format(".3~g")(value / 60)}m`

        return d3.format(this.tickFormat)
    }

    timeFormatter() {
        if (this.options.timezone) {
            return value => DateTime.fromJSDate(value).setZone(this.options.timezone).toFormat(this.options.time_format)
        }

        return d3.timeFormat(this.tickFormat)
    }
}
